import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCheck, Pencil, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ROLE_LABELS, STAFF_ROLES } from "@/lib/constants";

export default function Teilnehmer() {
  const [users, setUsers] = useState([]);
  const [courseMap, setCourseMap] = useState({});
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/courses").then(({ data }) => setCourseMap(Object.fromEntries(data.map((c) => [c.id, c.kurs_name])))).catch(() => {});
  }, []);

  const rolesOf = (u) => (u.roles && u.roles.length ? u.roles : u.role ? [u.role] : []);
  const participants = users.filter((u) => !rolesOf(u).some((r) => STAFF_ROLES.includes(r)));

  const save = async () => {
    try {
      await api.put(`/admin/users/${edit.id}`, { vorname: edit.vorname, nachname: edit.nachname, email: edit.email, status: edit.status });
      toast.success("Gespeichert."); setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggle = async (u) => {
    const status = u.status === "abgemeldet" ? "aktiv" : "abgemeldet";
    await api.put(`/admin/users/${u.id}`, { status });
    toast.success(status === "abgemeldet" ? "Abgemeldet." : "Wieder aktiv."); load();
  };
  const del = async (id) => {
    if (!window.confirm("Teilnehmer wirklich löschen?")) return;
    await api.delete(`/admin/users/${id}`);
    toast.success("Gelöscht."); load();
  };

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10">
      <div className="flex items-center gap-3 mb-2"><UserCheck className="text-primary" /><h1 className="text-3xl font-serif">Teilnehmer</h1></div>
      <p className="text-muted-foreground mb-8 text-sm">Eltern und weitere Teilnehmer – bearbeitbar, ab-/anmeldbar und löschbar.</p>

      <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>E-Mail</TableHead><TableHead>Status</TableHead><TableHead>Rolle</TableHead><TableHead>Kurse</TableHead><TableHead className="text-right">Aktionen</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {participants.map((u) => (
              <TableRow key={u.id} data-testid={`teilnehmer-row-${u.id}`}>
                <TableCell>{u.name}</TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${u.status === "abgemeldet" ? "bg-slate-100 text-slate-500" : "bg-green-50 text-green-700"}`}>{u.status || "aktiv"}</span></TableCell>
                <TableCell className="space-x-1">{rolesOf(u).map((r) => <span key={r} className="text-xs px-2 py-0.5 rounded-full bg-accent text-accent-foreground">{ROLE_LABELS[r] || r}</span>)}</TableCell>
                <TableCell className="text-sm">{(u.course_ids || []).map((id) => courseMap[id] || id).join(", ") || "—"}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button variant="ghost" size="sm" onClick={() => setEdit({ id: u.id, vorname: u.vorname || "", nachname: u.nachname || "", email: u.email, status: u.status || "aktiv" })} data-testid={`teilnehmer-edit-${u.id}`}><Pencil size={16} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => toggle(u)} data-testid={`teilnehmer-toggle-${u.id}`}>{u.status === "abgemeldet" ? "Anmelden" : "Abmelden"}</Button>
                  <Button variant="ghost" size="sm" onClick={() => del(u.id)} data-testid={`teilnehmer-delete-${u.id}`}><Trash2 size={16} className="text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {participants.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Noch keine Teilnehmer.</TableCell></TableRow>}
          </TableBody>
        </Table>
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Teilnehmer bearbeiten</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Vorname</Label><Input value={edit.vorname} onChange={(e) => setEdit({ ...edit, vorname: e.target.value })} data-testid="teilnehmer-edit-vorname" /></div>
                <div className="space-y-2"><Label>Nachname</Label><Input value={edit.nachname} onChange={(e) => setEdit({ ...edit, nachname: e.target.value })} data-testid="teilnehmer-edit-nachname" /></div>
              </div>
              <div className="space-y-2"><Label>E-Mail</Label><Input type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} data-testid="teilnehmer-edit-email" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={save} data-testid="teilnehmer-save-button">Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
